/**
 * Movement analyzer registry. Each analyzer accepts the array of
 * captured-per-frame pose data and returns a flat `metrics` object
 * (numbers only — no narrative text). The metrics are then sent to
 * the backend `/api/video-analysis/interpret` endpoint where Claude
 * compares them to the movement's ideal profile and generates the
 * coaching report.
 *
 * Frame shape (produced by useVideoMovementAnalyzer):
 *   { t: seconds, landmarks2D: [{x,y,visibility}, ...], landmarks3D: [...]|null }
 */

// ─── shared geometry helpers ─────────────────────────────────────
const angle2D = (a, b, c) => {
  if (!a || !b || !c) return null;
  const v1x = a.x - b.x, v1y = a.y - b.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (m1 < 1e-6 || m2 < 1e-6) return null;
  const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
};

const LM = {
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
};

const tripletAngle = (lm, a, b, c) => angle2D(lm[a], lm[b], lm[c]);

const stdDev = (arr) => {
  if (arr.length < 2) return 0;
  const m = arr.reduce((x, y) => x + y, 0) / arr.length;
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
};

const safeMax = (arr) => (arr.length ? Math.max(...arr) : null);
const safeMin = (arr) => (arr.length ? Math.min(...arr) : null);
const safeAvg = (arr) => (arr.length ? arr.reduce((x, y) => x + y, 0) / arr.length : null);
const round = (v, d = 1) => (v == null ? null : Math.round(v * 10 ** d) / 10 ** d);

// Decide camera view from landmark visibility (averaged across frames)
const detectView = (frames) => {
  if (!frames.length) return "side";
  let lShoulder = 0, rShoulder = 0, n = 0;
  for (const f of frames) {
    const ls = f.landmarks2D?.[LM.L_SHOULDER];
    const rs = f.landmarks2D?.[LM.R_SHOULDER];
    if (ls && rs) { lShoulder += ls.visibility || 0; rShoulder += rs.visibility || 0; n++; }
  }
  if (!n) return "side";
  const l = lShoulder / n, r = rShoulder / n;
  if (l > 0.6 && r > 0.6 && Math.abs(l - r) < 0.15) return "front";
  return "side";
};

// Rep detection: find peaks/valleys on a 1D angle timeseries.
// Returns array of { startIdx, peakIdx, endIdx, peakValue }
const detectReps = (series, { phase = "min", minPeakDelta = 25, minSeparationFrames = 5 } = {}) => {
  const vals = series.map(v => (v == null ? NaN : v));
  if (vals.length < 5) return [];
  // Smooth lightly with 3-point window
  const sm = vals.map((v, i) => {
    const a = vals[i - 1], c = vals[i + 1];
    const window = [v, a, c].filter(Number.isFinite);
    return window.length ? window.reduce((x, y) => x + y) / window.length : NaN;
  });
  const reps = [];
  let lastPeakIdx = -minSeparationFrames;
  let baseline = phase === "min" ? safeMax(sm.filter(Number.isFinite)) : safeMin(sm.filter(Number.isFinite));
  if (baseline == null) return [];
  for (let i = 2; i < sm.length - 2; i++) {
    if (!Number.isFinite(sm[i])) continue;
    const isPeak = phase === "min"
      ? sm[i] < sm[i - 1] && sm[i] <= sm[i - 2] && sm[i] < sm[i + 1] && sm[i] <= sm[i + 2]
      : sm[i] > sm[i - 1] && sm[i] >= sm[i - 2] && sm[i] > sm[i + 1] && sm[i] >= sm[i + 2];
    if (!isPeak) continue;
    const delta = phase === "min" ? baseline - sm[i] : sm[i] - baseline;
    if (delta < minPeakDelta) continue;
    if (i - lastPeakIdx < minSeparationFrames) continue;
    reps.push({ peakIdx: i, peakValue: sm[i] });
    lastPeakIdx = i;
  }
  return reps;
};

// ─── per-movement analyzers ──────────────────────────────────────

const analyzeSquat = (frames) => {
  const view = detectView(frames);
  // Track average knee + hip angle per frame
  const kneeSeries = frames.map(f => {
    const l = tripletAngle(f.landmarks2D, LM.L_HIP, LM.L_KNEE, LM.L_ANKLE);
    const r = tripletAngle(f.landmarks2D, LM.R_HIP, LM.R_KNEE, LM.R_ANKLE);
    const vals = [l, r].filter(v => v != null);
    return vals.length ? vals.reduce((x, y) => x + y) / vals.length : null;
  });
  const hipSeries = frames.map(f => {
    const l = tripletAngle(f.landmarks2D, LM.L_SHOULDER, LM.L_HIP, LM.L_KNEE);
    const r = tripletAngle(f.landmarks2D, LM.R_SHOULDER, LM.R_HIP, LM.R_KNEE);
    const vals = [l, r].filter(v => v != null);
    return vals.length ? vals.reduce((x, y) => x + y) / vals.length : null;
  });
  const trunkSeries = frames.map(f => {
    // Trunk lean = angle of the shoulder→hip line vs. vertical (gravity).
    // Use midpoints of L+R shoulders and L+R hips for robustness.
    // Image coords have y going DOWN, so "shoulder above hip" means hip.y > shoulder.y.
    // 0° = perfectly upright torso. ~20–30° = typical squat bottom. >40° = excessive fwd lean.
    const lm = f.landmarks2D;
    if (!lm) return null;
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    const lh = lm[LM.L_HIP],      rh = lm[LM.R_HIP];
    // Average whichever sides are present (visibility-tolerant)
    const pickPair = (a, b) => {
      const okA = a && (a.visibility == null || a.visibility >= 0.2);
      const okB = b && (b.visibility == null || b.visibility >= 0.2);
      if (okA && okB) return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (okA) return a;
      if (okB) return b;
      return null;
    };
    const shoulder = pickPair(ls, rs);
    const hip = pickPair(lh, rh);
    if (!shoulder || !hip) return null;
    const dx = shoulder.x - hip.x;          // horizontal offset
    const dy = hip.y - shoulder.y;          // positive when shoulder above hip
    if (dy < 0.05) return null;             // body crumpled / unreliable
    return Math.abs(Math.atan2(dx, dy)) * 180 / Math.PI;
  });
  const reps = detectReps(kneeSeries, { phase: "min", minPeakDelta: 30, minSeparationFrames: 6 });
  const validReps = reps.filter(r => Number.isFinite(r.peakValue));
  // Per-rep peak hip & knee
  const hipPeaks = validReps.map(r => hipSeries[r.peakIdx]).filter(v => v != null);
  const kneePeaks = validReps.map(r => r.peakValue);
  const trunkPeaks = validReps.map(r => trunkSeries[r.peakIdx]).filter(v => v != null);
  // Asymmetry: L vs R at each rep's peak
  const asymDeltas = validReps.map(r => {
    const lK = tripletAngle(frames[r.peakIdx].landmarks2D, LM.L_HIP, LM.L_KNEE, LM.L_ANKLE);
    const rK = tripletAngle(frames[r.peakIdx].landmarks2D, LM.R_HIP, LM.R_KNEE, LM.R_ANKLE);
    return lK != null && rK != null ? Math.abs(lK - rK) : null;
  }).filter(v => v != null);
  // Tempo: time between successive rep peaks
  const tempos = [];
  for (let i = 1; i < validReps.length; i++) {
    tempos.push(frames[validReps[i].peakIdx].t - frames[validReps[i - 1].peakIdx].t);
  }
  return {
    rep_count: validReps.length,
    view_detected: view,
    hip_flexion_peak_deg: round(safeMin(hipPeaks), 1),
    hip_flexion_peak_deg_avg: round(safeAvg(hipPeaks), 1),
    knee_flexion_peak_deg: round(safeMin(kneePeaks), 1),
    trunk_lean_max_deg: round(safeMax(trunkPeaks), 1),
    trunk_lean_avg_deg: round(safeAvg(trunkPeaks), 1),
    asymmetry_max_pct: round(safeMax(asymDeltas) ?? 0, 1),
    asymmetry_avg_pct: round(safeAvg(asymDeltas) ?? 0, 1),
    rep_tempo_avg_sec: round(safeAvg(tempos), 2),
    tempo_consistency_max_std_pct: round(tempos.length > 1 ? (stdDev(tempos) / safeAvg(tempos)) * 100 : 0, 1),
    rep_indices: validReps.map(r => r.peakIdx),
    rep_timestamps: validReps.map(r => round(frames[r.peakIdx].t, 2)),
    problem_moments: (() => {
      const moments = [];
      // Worst trunk lean rep > 30° threshold (real trunk-vs-vertical angle)
      if (trunkPeaks.length) {
        const idx = trunkPeaks.indexOf(safeMax(trunkPeaks));
        if (idx >= 0 && trunkPeaks[idx] > 30) {
          moments.push({
            t: round(frames[validReps[idx].peakIdx].t, 2),
            label: `Trunk lean spiked to ${Math.round(trunkPeaks[idx])}°`,
            severity: trunkPeaks[idx] > 45 ? 'high' : 'medium',
          });
        }
      }
      // Worst asymmetry rep > 5° threshold
      if (asymDeltas.length) {
        const idx = asymDeltas.indexOf(safeMax(asymDeltas));
        if (idx >= 0 && asymDeltas[idx] > 5) {
          moments.push({
            t: round(frames[validReps[idx].peakIdx].t, 2),
            label: `L/R knee asymmetry ${Math.round(asymDeltas[idx])}°`,
            severity: asymDeltas[idx] > 12 ? 'high' : 'medium',
          });
        }
      }
      // Shallowest rep (knee flexion above ideal max = not deep enough)
      if (kneePeaks.length) {
        const idx = kneePeaks.indexOf(safeMax(kneePeaks));
        if (idx >= 0 && kneePeaks[idx] > 110) {
          moments.push({
            t: round(frames[validReps[idx].peakIdx].t, 2),
            label: `Shallow rep (knee ${Math.round(kneePeaks[idx])}°)`,
            severity: 'low',
          });
        }
      }
      return moments;
    })(),
  };
};

const analyzeGeneric = (frames) => {
  const view = detectView(frames);
  // ROM per joint
  const jointSeries = {
    shoulder_L: frames.map(f => tripletAngle(f.landmarks2D, LM.L_HIP, LM.L_SHOULDER, LM.L_WRIST)),
    shoulder_R: frames.map(f => tripletAngle(f.landmarks2D, LM.R_HIP, LM.R_SHOULDER, LM.R_WRIST)),
    elbow_L:    frames.map(f => tripletAngle(f.landmarks2D, LM.L_SHOULDER, LM.L_ELBOW, LM.L_WRIST)),
    elbow_R:    frames.map(f => tripletAngle(f.landmarks2D, LM.R_SHOULDER, LM.R_ELBOW, LM.R_WRIST)),
    hip_L:      frames.map(f => tripletAngle(f.landmarks2D, LM.L_SHOULDER, LM.L_HIP, LM.L_KNEE)),
    hip_R:      frames.map(f => tripletAngle(f.landmarks2D, LM.R_SHOULDER, LM.R_HIP, LM.R_KNEE)),
    knee_L:     frames.map(f => tripletAngle(f.landmarks2D, LM.L_HIP, LM.L_KNEE, LM.L_ANKLE)),
    knee_R:     frames.map(f => tripletAngle(f.landmarks2D, LM.R_HIP, LM.R_KNEE, LM.R_ANKLE)),
  };
  const rom = {}; const variance = {};
  let mostActive = null, mostActiveVar = 0;
  for (const [k, s] of Object.entries(jointSeries)) {
    const clean = s.filter(v => v != null);
    if (clean.length < 3) continue;
    rom[k] = round(safeMax(clean) - safeMin(clean), 1);
    variance[k] = round(stdDev(clean), 1);
    if (variance[k] > mostActiveVar) { mostActiveVar = variance[k]; mostActive = k; }
  }
  // Asymmetry per pair
  const asymPairs = ["shoulder", "elbow", "hip", "knee"];
  const asymmetry = {};
  for (const p of asymPairs) {
    if (rom[`${p}_L`] != null && rom[`${p}_R`] != null) {
      const avg = (rom[`${p}_L`] + rom[`${p}_R`]) / 2;
      asymmetry[p] = avg > 0 ? round((Math.abs(rom[`${p}_L`] - rom[`${p}_R`]) / avg) * 100, 1) : 0;
    }
  }
  // Detect reps on the most-active joint
  const reps = mostActive ? detectReps(jointSeries[mostActive], { phase: "min", minPeakDelta: 20 }) : [];
  const tempos = [];
  for (let i = 1; i < reps.length; i++) {
    tempos.push(frames[reps[i].peakIdx].t - frames[reps[i - 1].peakIdx].t);
  }
  return {
    rep_count: reps.length,
    view_detected: view,
    most_active_joint: mostActive,
    most_active_variance_deg: mostActiveVar,
    rom_per_joint: rom,
    asymmetry_pct_per_pair: asymmetry,
    worst_asymmetry_pct: round(Math.max(0, ...Object.values(asymmetry)), 1),
    rep_tempo_avg_sec: round(safeAvg(tempos), 2),
    tempo_consistency_std_pct: round(tempos.length > 1 ? (stdDev(tempos) / safeAvg(tempos)) * 100 : 0, 1),
    rep_indices: reps.map(r => r.peakIdx),
    rep_timestamps: reps.map(r => round(frames[r.peakIdx].t, 2)),
    problem_moments: (() => {
      const moments = [];
      // Worst-asymmetry pair → tag with the timestamp of the most-extreme rep
      const worstPair = Object.entries(asymmetry).sort((a, b) => b[1] - a[1])[0];
      if (worstPair && worstPair[1] > 8 && reps.length) {
        // pick the rep where the most-active joint hit its peak
        const idx = reps[Math.floor(reps.length / 2)]?.peakIdx;
        if (idx != null) {
          moments.push({
            t: round(frames[idx].t, 2),
            label: `${worstPair[0]} asymmetry ${Math.round(worstPair[1])}%`,
            severity: worstPair[1] > 15 ? 'high' : 'medium',
          });
        }
      }
      return moments;
    })(),
  };
};

// Registry: maps catalog `analyzer` key → function
export const ANALYZERS = {
  squat: analyzeSquat,
  // Push-up, lunge, plank, deadlift, shoulderPress — for v1 these fall back
  // to the generic analyzer. Their movement profiles in the backend catalog
  // still give Claude movement-specific narration. Dedicated analyzers can
  // be promoted from generic later without any UI changes.
  pushup: analyzeGeneric,
  lunge: analyzeGeneric,
  plank: analyzeGeneric,
  deadlift: analyzeGeneric,
  shoulderPress: analyzeGeneric,
  generic: analyzeGeneric,
};

export const analyzeMovement = (analyzerKey, frames) => {
  const fn = ANALYZERS[analyzerKey] || analyzeGeneric;
  return fn(frames);
};

export default ANALYZERS;
